import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { useDispatch } from "react-redux";
import {
  updateCategoryByIdThunk,
  updateCategoryOrderThunk,
} from "../../../store/thunks/categoryThunks";
import Button from "../../../components/common/Button";
import Badge from "../../../components/common/Badge";
import {
  ChevronRightIcon,
  ChevronDownIcon,
  FolderIcon,
  FolderOpenIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  EyeIcon,
  ArrowsExpandIcon,
  XIcon,
  MinusIcon,
  HomeIcon,
  SwitchHorizontalIcon,
} from "@heroicons/react/outline";

// Tree line component for visual hierarchy
const TreeLine = ({ level, isLast, hasChildren, isExpanded, isMobile }) => {
  if (level === 0) return null;

  const lineColor = "border-gray-300";

  return (
    <div
      className="absolute left-0 top-0 bottom-0 pointer-events-none"
      style={{ width: `${isMobile ? level * 16 : level * 24}px` }}
    >
      <div
        className={`absolute ${lineColor} border-l`}
        style={{
          left: `${isMobile ? (level - 1) * 16 + 8 : (level - 1) * 24 + 12}px`,
          top: 0,
          bottom: isLast ? "50%" : 0,
        }}
      />
      <div
        className={`absolute ${lineColor} border-t`}
        style={{
          left: `${isMobile ? (level - 1) * 16 + 8 : (level - 1) * 24 + 12}px`,
          top: "50%",
          width: `${isMobile ? 8 : 12}px`,
        }}
      />
    </div>
  );
};

const CategoryTreeNode = ({
  category,
  level = 0,
  isLast = false,
  onAction,
  expandedNodes,
  onToggleExpand,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  isDragging,
  dragOverId,
  isMobile,
  selectedNodeId,
  onSelectNode,
  onNavigateToCategory,
  maxVisibleLevel,
  // Move mode (works on both mobile and desktop)
  moveModeCategory,
  onStartMoveMode,
  onCancelMoveMode,
  onMoveToCategory,
  // NEW: Global dragging state for CSS class
  isAnyDragging,
}) => {
  const hasChildren = category.children && category.children.length > 0;
  const isExpanded = expandedNodes.includes(category._id);
  const isActive = category.display?.isActive;
  const isFeatured = category.display?.isFeatured;
  const productCount = category.productCount || 0;
  const isSelected = selectedNodeId === category._id;
  const isBeingDragged = isDragging === category._id;
  const isDropTarget = dragOverId === category._id;

  // Move mode states (now works on both mobile and desktop)
  const isInMoveMode = moveModeCategory !== null;
  const isBeingMoved = moveModeCategory?._id === category._id;
  const canBeMovedTo =
    isInMoveMode && !isBeingMoved && moveModeCategory?._id !== category._id;

  const indentPerLevel = isMobile ? 16 : 24;
  const baseIndent = isMobile ? 8 : 12;
  const indentPx = level * indentPerLevel + baseIndent;

  const shouldShowDiveIn = isMobile && level >= maxVisibleLevel && hasChildren;

  const handleToggle = (e) => {
    e.stopPropagation();
    if (hasChildren) {
      onToggleExpand(category._id);
    }
  };

  const handleNodeClick = (e) => {
    e.stopPropagation();

    // If in move mode, clicking a node moves the category here (both mobile and desktop)
    if (isInMoveMode && canBeMovedTo) {
      onMoveToCategory(moveModeCategory._id, category._id);
      return;
    }

    if (isMobile) {
      onSelectNode(isSelected ? null : category._id);
    } else if (hasChildren) {
      onToggleExpand(category._id);
    }
  };

  const handleStartMove = (e) => {
    e.stopPropagation();
    onSelectNode(null);
    onStartMoveMode(category);
  };

  const handleDiveIn = (e) => {
    e.stopPropagation();
    onNavigateToCategory(category);
  };

  // FIXED: Drag handlers for desktop
  const handleDragStartOnHandle = (e) => {
    if (isMobile) {
      e.preventDefault();
      return;
    }

    console.log("🎯 DRAG START:", category.name);

    // Set drag data immediately
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", category._id);

    // Use setTimeout to allow the drag image to be captured before changing state
    setTimeout(() => {
      onDragStart(category);
    }, 0);
  };

  const handleDragEndOnHandle = (e) => {
    console.log("🏁 DRAG END:", category.name);
    onDragEnd();
  };

  const handleRowDragEnter = (e) => {
    if (isMobile || isBeingDragged) return;
    e.preventDefault();
    e.stopPropagation();
    onDragOver(category._id);
  };

  const handleRowDragOver = (e) => {
    if (isMobile || isBeingDragged) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
  };

  const handleRowDragLeave = (e) => {
    if (isMobile) return;
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      onDragOver(null);
    }
  };

  const handleRowDrop = (e) => {
    if (isMobile) return;
    e.preventDefault();
    e.stopPropagation();

    const draggedId = e.dataTransfer.getData("text/plain");
    console.log("💧 DROP:", draggedId, "onto", category.name);

    if (draggedId && draggedId !== category._id) {
      onDrop(draggedId, category._id);
    }
  };

  const handleAction = (action) => (e) => {
    e.stopPropagation();
    onSelectNode(null);
    onAction(category._id, action);
  };

  const LevelBadge = () => {
    if (level < 3) return null;
    return (
      <span className="hidden sm:inline-flex items-center justify-center w-5 h-5 text-[10px] font-medium text-gray-500 bg-gray-100 rounded-full">
        L{level + 1}
      </span>
    );
  };

  return (
    <div
      style={{
        position: "relative",
        userSelect: isAnyDragging ? "none" : "auto",
      }}
    >
      <TreeLine
        level={level}
        isLast={isLast}
        hasChildren={hasChildren}
        isExpanded={isExpanded}
        isMobile={isMobile}
      />

      {/* Main Node Row */}
      <div
        className={`
          group flex items-center gap-1.5 sm:gap-2 rounded-lg transition-all cursor-pointer relative
          ${
            isBeingDragged || isBeingMoved
              ? "opacity-40 scale-95 bg-warning/20"
              : ""
          }
          ${
            isDropTarget && !isBeingDragged
              ? "bg-primary/15 ring-2 ring-primary ring-offset-1"
              : ""
          }
          ${
            canBeMovedTo
              ? "bg-success/5 hover:bg-success/15 ring-1 ring-success/30 cursor-pointer"
              : ""
          }
          ${
            isSelected && !isDropTarget && !canBeMovedTo
              ? "bg-primary/5 ring-1 ring-primary/30"
              : ""
          }
          ${
            !isBeingDragged &&
            !isBeingMoved &&
            !isDropTarget &&
            !isSelected &&
            !canBeMovedTo
              ? "hover:bg-gray-50 active:bg-gray-100"
              : ""
          }
          py-2.5 sm:py-3 pr-2 sm:pr-3
        `}
        style={{
          paddingLeft: `${indentPx}px`,
          // Disable pointer events on the row when it's being dragged
          pointerEvents: isBeingDragged ? "none" : "auto",
        }}
        onDragEnter={handleRowDragEnter}
        onDragOver={handleRowDragOver}
        onDragLeave={handleRowDragLeave}
        onDrop={handleRowDrop}
        onClick={handleNodeClick}
      >
        {/* FIXED: Drag Handle - Desktop Only */}
        {!isMobile && !isInMoveMode && (
          <div
            draggable="true"
            onDragStart={handleDragStartOnHandle}
            onDragEnd={handleDragEndOnHandle}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            className="drag-handle flex items-center justify-center flex-shrink-0 hover:bg-gray-200 active:cursor-grabbing select-none"
            style={{
              width: "24px",
              height: "24px",
              backgroundColor: "#f3f4f6",
              borderRadius: "4px",
              marginRight: "4px",
              border: "1px solid #e5e7eb",
              transition: "all 0.15s",
              cursor: "grab",
              touchAction: "none",
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitUserDrag: "element",
            }}
            title="Drag to move category"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              style={{ pointerEvents: "none" }}
            >
              <circle cx="5" cy="3" r="1.5" fill="#9ca3af" />
              <circle cx="11" cy="3" r="1.5" fill="#9ca3af" />
              <circle cx="5" cy="8" r="1.5" fill="#9ca3af" />
              <circle cx="11" cy="8" r="1.5" fill="#9ca3af" />
              <circle cx="5" cy="13" r="1.5" fill="#9ca3af" />
              <circle cx="11" cy="13" r="1.5" fill="#9ca3af" />
            </svg>
          </div>
        )}

        {/* Move target indicator when in move mode */}
        {isInMoveMode && canBeMovedTo && (
          <div className="flex-shrink-0 mr-1">
            <div className="w-6 h-6 bg-success/20 rounded-full flex items-center justify-center">
              <PlusIcon className="w-4 h-4 text-success" />
            </div>
          </div>
        )}

        {/* Expand/Collapse Icon */}
        <button
          onClick={handleToggle}
          className={`w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded flex-shrink-0 
            ${
              hasChildren ? "hover:bg-gray-200 active:bg-gray-300" : "opacity-0"
            }`}
          disabled={!hasChildren}
        >
          {hasChildren &&
            (isExpanded ? (
              <ChevronDownIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600" />
            ) : (
              <ChevronRightIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600" />
            ))}
        </button>

        {/* Folder Icon */}
        <div className="flex-shrink-0">
          {isExpanded && hasChildren ? (
            <FolderOpenIcon
              className={`w-4 h-4 sm:w-5 sm:h-5 ${
                isActive ? "text-secondary" : "text-gray-400"
              }`}
            />
          ) : (
            <FolderIcon
              className={`w-4 h-4 sm:w-5 sm:h-5 ${
                isActive ? "text-secondary" : "text-gray-400"
              }`}
            />
          )}
        </div>

        {/* Category Name & Info */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span
              className={`font-medium text-sm sm:text-base truncate ${
                !isActive ? "text-gray-400" : "text-gray-900"
              }`}
            >
              {category.name}
            </span>
            <LevelBadge />
            <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
              {!isActive && (
                <span className="text-xs text-danger bg-danger/10 px-1.5 py-0.5 rounded">
                  Inactive
                </span>
              )}
              {isFeatured && (
                <span className="text-xs text-secondary-dark bg-secondary/20 px-1.5 py-0.5 rounded">
                  ★
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5">
            {productCount > 0 && (
              <span className="text-[10px] sm:text-xs text-gray-500">
                {productCount} products
              </span>
            )}
            {hasChildren && (
              <span className="text-[10px] sm:text-xs text-gray-400">
                {category.children.length} sub
              </span>
            )}
          </div>
        </div>

        {/* Mobile: Dive in button for deep categories */}
        {shouldShowDiveIn && (
          <button
            onClick={handleDiveIn}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary bg-primary/10 rounded-lg active:bg-primary/20"
          >
            <span>Enter</span>
            <ChevronRightIcon className="w-3 h-3" />
          </button>
        )}

        {/* Desktop Actions - Now includes Move button */}
        {!isInMoveMode && (
          <div className="hidden sm:flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            <button
              onClick={handleAction("view")}
              className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
              title="View"
            >
              <EyeIcon className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={handleAction("add")}
              className="p-1.5 hover:bg-success/20 rounded-lg transition-colors"
              title="Add Subcategory"
            >
              <PlusIcon className="w-4 h-4 text-success" />
            </button>
            <button
              onClick={handleAction("edit")}
              className="p-1.5 hover:bg-primary/20 rounded-lg transition-colors"
              title="Edit"
            >
              <PencilIcon className="w-4 h-4 text-primary" />
            </button>
            {/* Move Button - Now on desktop too! */}
            <button
              onClick={handleStartMove}
              className="p-1.5 hover:bg-warning/20 rounded-lg transition-colors"
              title="Move to different parent"
            >
              <SwitchHorizontalIcon className="w-4 h-4 text-warning-dark" />
            </button>
            <button
              onClick={handleAction("delete")}
              className="p-1.5 hover:bg-danger/20 rounded-lg transition-colors"
              title="Delete"
            >
              <TrashIcon className="w-4 h-4 text-danger" />
            </button>
          </div>
        )}

        {/* Mobile: Status dots */}
        {!shouldShowDiveIn && (
          <div className="sm:hidden flex items-center gap-1 flex-shrink-0">
            {!isActive && (
              <span className="w-1.5 h-1.5 bg-danger rounded-full"></span>
            )}
            {isFeatured && (
              <span className="w-1.5 h-1.5 bg-secondary rounded-full"></span>
            )}
          </div>
        )}
      </div>

      {/* Mobile Action Sheet */}
      {isMobile && isSelected && (
        <div
          className="mx-2 mb-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden animate-fadeIn"
          style={{ marginLeft: `${indentPx}px` }}
        >
          <div className="px-3 py-2.5 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-900 text-sm truncate max-w-[200px]">
                {category.name}
              </span>
              <button
                onClick={() => onSelectNode(null)}
                className="p-1 hover:bg-gray-200 rounded-full"
              >
                <XIcon className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              {isActive ? (
                <span className="text-[10px] text-success bg-success/10 px-1.5 py-0.5 rounded-full">
                  Active
                </span>
              ) : (
                <span className="text-[10px] text-danger bg-danger/10 px-1.5 py-0.5 rounded-full">
                  Inactive
                </span>
              )}
              {isFeatured && (
                <span className="text-[10px] text-secondary-dark bg-secondary/20 px-1.5 py-0.5 rounded-full">
                  Featured
                </span>
              )}
              {category.display?.showInMenu && (
                <span className="text-[10px] text-gray-600 bg-gray-200 px-1.5 py-0.5 rounded-full">
                  Menu
                </span>
              )}
              {level > 0 && (
                <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
                  Level {level + 1}
                </span>
              )}
            </div>
          </div>
          <div className="p-2">
            <div className="grid grid-cols-5 gap-1">
              <button
                onClick={handleAction("view")}
                className="flex flex-col items-center gap-1 p-2 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <EyeIcon className="w-4 h-4 text-gray-700" />
                </div>
                <span className="text-[10px] font-medium text-gray-700">
                  View
                </span>
              </button>
              <button
                onClick={handleAction("add")}
                className="flex flex-col items-center gap-1 p-2 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center">
                  <PlusIcon className="w-4 h-4 text-success" />
                </div>
                <span className="text-[10px] font-medium text-success">
                  Add
                </span>
              </button>
              <button
                onClick={handleAction("edit")}
                className="flex flex-col items-center gap-1 p-2 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <PencilIcon className="w-4 h-4 text-primary" />
                </div>
                <span className="text-[10px] font-medium text-primary">
                  Edit
                </span>
              </button>
              <button
                onClick={handleStartMove}
                className="flex flex-col items-center gap-1 p-2 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-warning/10 rounded-full flex items-center justify-center">
                  <SwitchHorizontalIcon className="w-4 h-4 text-warning-dark" />
                </div>
                <span className="text-[10px] font-medium text-warning-dark">
                  Move
                </span>
              </button>
              <button
                onClick={handleAction("delete")}
                className="flex flex-col items-center gap-1 p-2 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-danger/10 rounded-full flex items-center justify-center">
                  <TrashIcon className="w-4 h-4 text-danger" />
                </div>
                <span className="text-[10px] font-medium text-danger">
                  Delete
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Children */}
      {hasChildren && isExpanded && (!isMobile || level < maxVisibleLevel) && (
        <div>
          {category.children.map((child, index) => (
            <CategoryTreeNode
              key={child._id}
              category={child}
              level={level + 1}
              isLast={index === category.children.length - 1}
              onAction={onAction}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDragEnd={onDragEnd}
              onDrop={onDrop}
              isDragging={isDragging}
              dragOverId={dragOverId}
              isMobile={isMobile}
              selectedNodeId={selectedNodeId}
              onSelectNode={onSelectNode}
              onNavigateToCategory={onNavigateToCategory}
              maxVisibleLevel={maxVisibleLevel}
              moveModeCategory={moveModeCategory}
              onStartMoveMode={onStartMoveMode}
              onCancelMoveMode={onCancelMoveMode}
              onMoveToCategory={onMoveToCategory}
              isAnyDragging={isAnyDragging}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CategoryBreadcrumb = ({ path, onNavigate, onGoHome, currentCount }) => {
  return (
    <div className="flex items-center gap-1 px-3 py-2 bg-gray-50 border-b border-gray-100 overflow-x-auto scrollbar-hide">
      <button
        onClick={onGoHome}
        className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg transition-colors flex-shrink-0 ${
          path.length === 0
            ? "text-primary bg-primary/10"
            : "text-gray-600 hover:text-primary hover:bg-gray-100"
        }`}
      >
        <HomeIcon className="w-3.5 h-3.5" />
        <span>Root</span>
      </button>
      {path.map((cat, index) => (
        <React.Fragment key={cat._id}>
          <ChevronRightIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
          <button
            onClick={() => onNavigate(index)}
            className={`px-2 py-1.5 text-xs font-medium rounded-lg transition-colors flex-shrink-0 max-w-[120px] truncate ${
              index === path.length - 1
                ? "text-primary bg-primary/10"
                : "text-gray-600 hover:text-primary hover:bg-gray-100"
            }`}
          >
            {cat.name}
          </button>
        </React.Fragment>
      ))}
      <span className="ml-auto text-[10px] text-gray-400 flex-shrink-0">
        {currentCount} items
      </span>
    </div>
  );
};

const CategoryTreeView = ({ categories, onAction, onReorder }) => {
  const dispatch = useDispatch();
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [draggedCategory, setDraggedCategory] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [navigationPath, setNavigationPath] = useState([]);
  const [moveModeCategory, setMoveModeCategory] = useState(null);
  const containerRef = useRef(null);
  const treeContainerRef = useRef(null);

  const maxVisibleLevel = isMobile ? 3 : 100;

  // Track if any dragging is happening for CSS class
  const isAnyDragging = draggedCategory !== null;

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 640;
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // FIXED: Add/remove dragging class to body to prevent text selection globally
  useEffect(() => {
    if (isAnyDragging) {
      document.body.classList.add("dragging-active");
      document.body.style.userSelect = "none";
    } else {
      document.body.classList.remove("dragging-active");
      document.body.style.userSelect = "";
    }

    return () => {
      document.body.classList.remove("dragging-active");
      document.body.style.userSelect = "";
    };
  }, [isAnyDragging]);

  useEffect(() => {
    if (!isMobile || !selectedNodeId || moveModeCategory) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSelectedNodeId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isMobile, selectedNodeId, moveModeCategory]);

  const fullTreeData = useMemo(() => {
    const buildTree = (items, parentId = null) => {
      return items
        .filter((item) => {
          const itemParentId = item.parentId?._id || item.parentId;
          return itemParentId === parentId;
        })
        .map((item) => ({
          ...item,
          children: buildTree(items, item._id),
        }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    };
    return buildTree(categories);
  }, [categories]);

  const treeData = useMemo(() => {
    if (navigationPath.length === 0) return fullTreeData;
    const findCategoryInTree = (nodes, targetId) => {
      if (!nodes || nodes.length === 0) return null;
      for (const node of nodes) {
        if (node._id === targetId) return node;
        if (node.children && node.children.length > 0) {
          const found = findCategoryInTree(node.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };
    const lastPathCategory = navigationPath[navigationPath.length - 1];
    const foundCategory = findCategoryInTree(
      fullTreeData,
      lastPathCategory._id
    );
    if (foundCategory?.children?.length > 0) return foundCategory.children;
    if (lastPathCategory.children?.length > 0) return lastPathCategory.children;
    return [];
  }, [fullTreeData, navigationPath]);

  const allExpandableNodeIds = useMemo(() => {
    const getExpandableIds = (nodes) => {
      const ids = [];
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          ids.push(node._id);
          ids.push(...getExpandableIds(node.children));
        }
      });
      return ids;
    };
    return getExpandableIds(treeData);
  }, [treeData]);

  const handleToggleExpand = useCallback((categoryId) => {
    setExpandedNodes((prev) => {
      if (prev.includes(categoryId))
        return prev.filter((id) => id !== categoryId);
      return [...prev, categoryId];
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    setExpandedNodes(allExpandableNodeIds);
  }, [allExpandableNodeIds]);

  const handleCollapseAll = useCallback(() => {
    setExpandedNodes([]);
  }, []);

  const handleDragStart = useCallback((category) => {
    console.log("📦 Setting dragged category:", category.name);
    setDraggedCategory(category);
  }, []);

  const handleDragOver = useCallback((categoryId) => {
    setDragOverId(categoryId);
  }, []);

  const handleDragEnd = useCallback(() => {
    console.log("🔚 Clearing drag state");
    setDraggedCategory(null);
    setDragOverId(null);
  }, []);

  const handleDropToRoot = useCallback(
    async (e) => {
      e.preventDefault();
      const draggedId = e.dataTransfer.getData("text/plain");
      if (!draggedId) return;

      setDragOverId(null);
      setDraggedCategory(null);

      const dragged = categories.find((c) => c._id === draggedId);
      if (!dragged) return;

      const draggedParentId = dragged.parentId?._id || dragged.parentId;
      if (!draggedParentId) return;

      try {
        await dispatch(
          updateCategoryByIdThunk({
            categoryId: draggedId,
            categoryData: { parentId: null },
          })
        );
        if (onReorder) onReorder();
      } catch (error) {
        console.error("Failed to move category to root:", error);
        alert("Failed to move category. Please try again.");
      }
    },
    [categories, dispatch, onReorder]
  );

  const handleDrop = useCallback(
    async (draggedId, targetId) => {
      setDragOverId(null);
      setDraggedCategory(null);

      if (draggedId === targetId) return;

      const dragged = categories.find((c) => c._id === draggedId);
      const target = categories.find((c) => c._id === targetId);
      if (!dragged || !target) return;

      const draggedParentId = dragged.parentId?._id || dragged.parentId;
      if (draggedParentId === targetId) return;

      const isMovingToDescendant = (() => {
        let current = target;
        const visited = new Set();
        while (current) {
          if (visited.has(current._id)) break;
          visited.add(current._id);
          const currentParentId = current.parentId?._id || current.parentId;
          if (currentParentId === draggedId) return true;
          current = categories.find((c) => c._id === currentParentId);
        }
        return false;
      })();

      try {
        if (isMovingToDescendant) {
          const draggedOldParentId =
            dragged.parentId?._id || dragged.parentId || null;
          const directChildren = categories.filter((c) => {
            const parentId = c.parentId?._id || c.parentId;
            return parentId === draggedId;
          });
          for (const child of directChildren) {
            await dispatch(
              updateCategoryByIdThunk({
                categoryId: child._id,
                categoryData: { parentId: draggedOldParentId },
              })
            );
          }
        }

        await dispatch(
          updateCategoryByIdThunk({
            categoryId: draggedId,
            categoryData: { parentId: targetId },
          })
        );
        if (onReorder) onReorder();
      } catch (error) {
        console.error("Failed to move category:", error);
        alert("Failed to move category. Please try again.");
      }
    },
    [categories, dispatch, onReorder]
  );

  // Move mode handlers
  const handleStartMoveMode = useCallback(
    (category) => {
      setMoveModeCategory(category);
      setExpandedNodes(allExpandableNodeIds);
    },
    [allExpandableNodeIds]
  );

  const handleCancelMoveMode = useCallback(() => {
    setMoveModeCategory(null);
  }, []);

  const handleMoveToCategory = useCallback(
    async (draggedId, targetId) => {
      setMoveModeCategory(null);
      if (draggedId === targetId) return;

      const dragged = categories.find((c) => c._id === draggedId);
      const target =
        targetId === "root" ? null : categories.find((c) => c._id === targetId);
      if (!dragged) return;
      if (targetId !== "root" && !target) return;

      const draggedParentId = dragged.parentId?._id || dragged.parentId;
      if (targetId === "root" && !draggedParentId) return;
      if (draggedParentId === targetId) return;

      const isMovingToDescendant = (() => {
        if (targetId === "root") return false;
        let current = target;
        const visited = new Set();
        while (current) {
          if (visited.has(current._id)) break;
          visited.add(current._id);
          const currentParentId = current.parentId?._id || current.parentId;
          if (currentParentId === draggedId) return true;
          current = categories.find((c) => c._id === currentParentId);
        }
        return false;
      })();

      try {
        if (isMovingToDescendant) {
          const draggedOldParentId =
            dragged.parentId?._id || dragged.parentId || null;
          const directChildren = categories.filter((c) => {
            const parentId = c.parentId?._id || c.parentId;
            return parentId === draggedId;
          });
          for (const child of directChildren) {
            await dispatch(
              updateCategoryByIdThunk({
                categoryId: child._id,
                categoryData: { parentId: draggedOldParentId },
              })
            );
          }
        }

        await dispatch(
          updateCategoryByIdThunk({
            categoryId: draggedId,
            categoryData: { parentId: targetId === "root" ? null : targetId },
          })
        );
        if (onReorder) onReorder();
      } catch (error) {
        console.error("Failed to move category:", error);
        alert("Failed to move category. Please try again.");
      }
    },
    [categories, dispatch, onReorder]
  );

  const handleNavigateToCategory = useCallback((category) => {
    setNavigationPath((prev) => [...prev, category]);
    if (category.children?.length > 0) {
      const childIdsWithChildren = category.children
        .filter((c) => c.children?.length > 0)
        .map((c) => c._id);
      setExpandedNodes(childIdsWithChildren);
    } else {
      setExpandedNodes([]);
    }
    if (treeContainerRef.current) {
      treeContainerRef.current.scrollTop = 0;
    }
  }, []);

  const handleNavigateToBreadcrumb = useCallback((index) => {
    setNavigationPath((prev) => prev.slice(0, index + 1));
  }, []);

  const handleGoHome = useCallback(() => {
    setNavigationPath([]);
  }, []);

  const stats = useMemo(() => {
    const total = categories.length;
    const active = categories.filter((c) => c.display?.isActive).length;
    const featured = categories.filter((c) => c.display?.isFeatured).length;
    return { total, active, featured };
  }, [categories]);

  const maxDepth = useMemo(() => {
    const getDepth = (nodes, currentDepth = 0) => {
      if (!nodes || nodes.length === 0) return currentDepth;
      return Math.max(
        ...nodes.map((n) => getDepth(n.children, currentDepth + 1))
      );
    };
    return getDepth(fullTreeData);
  }, [fullTreeData]);

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${
        isAnyDragging ? "dragging-active" : ""
      }`}
      ref={containerRef}
    >
      {/* Stats Header */}
      <div className="p-3 sm:p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="flex-1 grid grid-cols-4 gap-2 sm:gap-3">
            <div className="bg-gray-50 rounded-lg p-2 sm:p-3 text-center">
              <div className="flex items-center justify-center gap-1.5 mb-0.5">
                <FolderIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" />
                <span className="text-lg sm:text-2xl font-bold text-gray-900">
                  {stats.total}
                </span>
              </div>
              <div className="text-[9px] sm:text-xs text-gray-500 font-medium">
                Total
              </div>
            </div>
            <div className="bg-success/5 rounded-lg p-2 sm:p-3 text-center">
              <div className="flex items-center justify-center gap-1.5 mb-0.5">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <span className="text-lg sm:text-2xl font-bold text-success">
                  {stats.active}
                </span>
              </div>
              <div className="text-[9px] sm:text-xs text-success font-medium">
                Active
              </div>
            </div>
            <div className="bg-secondary/10 rounded-lg p-2 sm:p-3 text-center">
              <div className="flex items-center justify-center gap-1.5 mb-0.5">
                <span className="text-xs sm:text-sm">★</span>
                <span className="text-lg sm:text-2xl font-bold text-secondary-dark">
                  {stats.featured}
                </span>
              </div>
              <div className="text-[9px] sm:text-xs text-secondary-dark font-medium">
                Featured
              </div>
            </div>
            <div className="bg-primary/5 rounded-lg p-2 sm:p-3 text-center">
              <div className="flex items-center justify-center gap-1.5 mb-0.5">
                <div className="flex flex-col gap-0.5">
                  <div className="w-2 h-0.5 bg-primary rounded"></div>
                  <div className="w-3 h-0.5 bg-primary rounded ml-1"></div>
                  <div className="w-2 h-0.5 bg-primary rounded ml-2"></div>
                </div>
                <span className="text-lg sm:text-2xl font-bold text-primary">
                  {maxDepth}
                </span>
              </div>
              <div className="text-[9px] sm:text-xs text-primary font-medium">
                Levels
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:gap-2">
            <button
              onClick={handleExpandAll}
              className="p-2 sm:px-3 sm:py-2 text-sm text-gray-600 hover:bg-gray-100 active:bg-gray-200 rounded-lg border border-gray-200 transition-colors flex items-center justify-center gap-1.5"
              title="Expand All"
            >
              <ArrowsExpandIcon className="w-4 h-4" />
              <span className="hidden sm:inline text-xs">Expand</span>
            </button>
            <button
              onClick={handleCollapseAll}
              className="p-2 sm:px-3 sm:py-2 text-sm text-gray-600 hover:bg-gray-100 active:bg-gray-200 rounded-lg border border-gray-200 transition-colors flex items-center justify-center gap-1.5"
              title="Collapse All"
            >
              <MinusIcon className="w-4 h-4" />
              <span className="hidden sm:inline text-xs">Collapse</span>
            </button>
          </div>
        </div>
      </div>

      {/* Breadcrumb Navigation (for mobile deep drilling) */}
      {isMobile && (
        <CategoryBreadcrumb
          path={navigationPath}
          onNavigate={handleNavigateToBreadcrumb}
          onGoHome={handleGoHome}
          currentCount={treeData.length}
        />
      )}

      {/* Move Mode Bar */}
      {moveModeCategory && (
        <div className="px-3 py-2.5 bg-warning/10 border-b border-warning/30 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <SwitchHorizontalIcon className="w-4 h-4 text-warning-dark flex-shrink-0" />
            <span className="text-sm text-warning-dark truncate">
              Moving: <strong>{moveModeCategory.name}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => handleMoveToCategory(moveModeCategory._id, "root")}
              className="px-2 py-1 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors"
            >
              Move to Root
            </button>
            <button
              onClick={handleCancelMoveMode}
              className="px-2 py-1 text-xs font-medium text-danger bg-danger/10 rounded-lg hover:bg-danger/20 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Tree View */}
      <div
        ref={treeContainerRef}
        className="max-h-[50vh] sm:max-h-[55vh] overflow-y-auto overflow-x-hidden"
      >
        {/* Root Drop Zone - visible when dragging (desktop only) */}
        {draggedCategory && !isMobile && navigationPath.length === 0 && (
          <div
            className={`mx-3 mt-2 p-3 border-2 border-dashed rounded-lg text-center text-sm transition-all ${
              dragOverId === "root"
                ? "border-primary bg-primary/10 text-primary"
                : "border-gray-300 text-gray-500 hover:border-gray-400"
            }`}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragOverId("root");
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setDragOverId(null);
              }
            }}
            onDrop={handleDropToRoot}
          >
            <HomeIcon className="w-5 h-5 mx-auto mb-1" />
            Drop here to make root category
          </div>
        )}

        {treeData.length > 0 ? (
          <div className="py-2">
            {treeData.map((category, index) => (
              <CategoryTreeNode
                key={category._id}
                category={category}
                level={0}
                isLast={index === treeData.length - 1}
                onAction={onAction}
                expandedNodes={expandedNodes}
                onToggleExpand={handleToggleExpand}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDrop={handleDrop}
                isDragging={draggedCategory?._id}
                dragOverId={dragOverId}
                isMobile={isMobile}
                selectedNodeId={selectedNodeId}
                onSelectNode={setSelectedNodeId}
                onNavigateToCategory={handleNavigateToCategory}
                maxVisibleLevel={maxVisibleLevel}
                moveModeCategory={moveModeCategory}
                onStartMoveMode={handleStartMoveMode}
                onCancelMoveMode={handleCancelMoveMode}
                onMoveToCategory={handleMoveToCategory}
                isAnyDragging={isAnyDragging}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-10 sm:py-12">
            <FolderOpenIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">
              {navigationPath.length > 0
                ? "No subcategories here"
                : "No categories available"}
            </p>
            {navigationPath.length > 0 && (
              <button
                onClick={() => setNavigationPath([])}
                className="mt-2 text-sm text-primary hover:underline"
              >
                Go to root
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer Help */}
      <div className="px-3 sm:px-4 py-2 sm:py-2.5 border-t border-gray-100 bg-gray-50 text-[10px] sm:text-xs text-gray-500 text-center">
        {moveModeCategory ? (
          <span className="text-warning-dark">
            👆 Click a category to move here • Or click "Move to Root" /
            "Cancel"
          </span>
        ) : isMobile ? (
          <span>👆 Tap category for actions • Use "Enter" for deep levels</span>
        ) : (
          <span>
            ⋮ Drag handle or use Move button to reorganize • Hover for actions •
            Click to expand
          </span>
        )}
      </div>
    </div>
  );
};

export default CategoryTreeView;
